/**
 * highlightDom.ts — TypeScript-typed wrapper around the SHARED vanilla JS
 * highlight script (HIGHLIGHT_SCRIPT_SOURCE) defined in bridgeJs.ts.
 *
 * This module is the entry point used by React DOM consumers (web reader,
 * desktop Tauri reader) via the `@arianna/reader-render` package. The mobile
 * WebView reader gets the SAME script via `buildBridgeJs()`.
 *
 * One source. Two delivery channels. Zero duplication.
 *
 * On first call the script is injected as an inline <script> in the host
 * document, exposing `window.ariannaApplyHighlight`, `window.ariannaSelectedRange`,
 * etc.  Subsequent calls just proxy through those globals.
 */

import { HIGHLIGHT_SCRIPT_SOURCE } from './bridgeJs.js'
import type { ReaderRenderHighlight } from './types.js'

// ---------------------------------------------------------------------------
// Lazy injection
// ---------------------------------------------------------------------------

let injected = false

function ensureInjected(): void {
  if (injected) return
  if (typeof document === 'undefined') return
  const script = document.createElement('script')
  script.text = HIGHLIGHT_SCRIPT_SOURCE
  document.head.appendChild(script)
  injected = true
}

// ---------------------------------------------------------------------------
// Window typing — the globals installed by HIGHLIGHT_SCRIPT_SOURCE
// ---------------------------------------------------------------------------

interface AriannaTextPosition {
  node: Text
  offset: number
}

declare global {
  interface Window {
    ariannaNfc?: (s: string) => string
    ariannaTextPosition?: (root: Element, offset: number) => AriannaTextPosition | null
    ariannaApplyHighlight?: (h: ReaderRenderHighlight) => void
    ariannaClearHighlights?: (root?: Element) => void
    ariannaSetHighlights?: (highlights: ReadonlyArray<ReaderRenderHighlight>) => void
    ariannaSelectedRange?: () => SelectedRange | null
    ariannaSetTheme?: (theme: 'studiolo' | 'notturno') => void
  }
}

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export interface TextPosition {
  node: Text
  offset: number
}

export interface SelectedRange {
  type: 'selection'
  subParaId: string
  blockId: string
  startChar: number
  endChar: number
  text: string
}

// ---------------------------------------------------------------------------
// Public API — thin proxies over the injected globals
// ---------------------------------------------------------------------------

export function textPosition(root: Element, offset: number): TextPosition | null {
  ensureInjected()
  return window.ariannaTextPosition?.(root, offset) ?? null
}

export function applyHighlight(h: ReaderRenderHighlight): void {
  ensureInjected()
  window.ariannaApplyHighlight?.(h)
}

export function clearHighlights(root?: Element): void {
  ensureInjected()
  window.ariannaClearHighlights?.(root)
}

export function setHighlights(highlights: ReadonlyArray<ReaderRenderHighlight>): void {
  ensureInjected()
  window.ariannaSetHighlights?.(highlights)
}

export function selectedRangeInBlock(): SelectedRange | null {
  ensureInjected()
  return window.ariannaSelectedRange?.() ?? null
}
